import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { LearningProfile, TargetDetail, TargetType } from '../../services/learningApi';
import { ListField, stepLabel, useApis } from './common';

interface Props {
  targetType: TargetType;
  targetId: string;
  detail: TargetDetail;
  onChanged: () => void;
}

const EMPTY: LearningProfile = {
  summary: '',
  success_criteria: [],
  failure_modes: [],
  observation: { key_label: 'Input', item_label: 'result', key_examples: [], generalize: '', categories: [] },
  fact_topics: [],
  learn_nodes: [],
  inject: [],
  notes: '',
};

export const ProfileTab: React.FC<Props> = ({ targetType, targetId, detail, onChanged }) => {
  const apis = useApis();
  const saved = detail.profile;
  const [profile, setProfile] = useState<LearningProfile>(saved || EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [genOpen, setGenOpen] = useState(false);
  const [hints, setHints] = useState('');
  const [refine, setRefine] = useState(true);
  const [generating, setGenerating] = useState(false);

  const savedJson = JSON.stringify(saved || EMPTY);
  useEffect(() => setProfile(JSON.parse(savedJson)), [savedJson]);
  const dirty = useMemo(() => JSON.stringify(profile) !== savedJson, [profile, savedJson]);
  const nodes = detail.target.nodes || [];
  const aiNodes = nodes.filter((n) => n.kind === 'ai_function');
  const nodeLabel = (id: string | null) => (id ? stepLabel(nodes, id) : 'This function');
  const learnScope = detail.settings.scope.learn_nodes.length ? detail.settings.scope.learn_nodes : profile.learn_nodes;
  const injectScope = detail.settings.scope.inject_nodes;

  if (!detail.config) {
    return <Alert severity="info">Turn self-improvement on (or save settings) first — the profile is generated for this target when it's set up.</Alert>;
  }

  const set = <K extends keyof LearningProfile>(key: K, value: LearningProfile[K]) => {
    setMessage(null);
    setProfile((p) => ({ ...p, [key]: value }));
  };
  const setObs = <K extends keyof LearningProfile['observation']>(key: K, value: LearningProfile['observation'][K]) =>
    set('observation', { ...profile.observation, [key]: value });
  const setInject = (i: number, patch: Partial<LearningProfile['inject'][number]>) =>
    set('inject', profile.inject.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const insertField = (i: number, field: string) => {
    const current = profile.inject[i].query_template.trim();
    const token = `{{${field}}}`;
    setInject(i, { query_template: !current || current === '{{input}}' ? token : `${current} ${token}` });
  };
  const usedNodes = new Set(profile.inject.map((x) => x.node_id).filter(Boolean) as string[]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await apis.learning.saveProfile(targetType, targetId, profile);
      setProfile(res.profile);
      setMessage('Profile saved. The next cycle will use it.');
      onChanged();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const generate = async () => {
    setGenerating(true);
    setError(null);
    try {
      const refining = !!saved && refine;
      const res = await apis.learning.generateProfile(targetType, targetId, hints, refining);
      setProfile(res.profile);
      setGenOpen(false);
      setMessage(`${refining ? 'Profile updated' : 'New profile generated'} (${res.credits_used.toFixed(2)} credits).`);
      onChanged();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} justifyContent="space-between">
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              Learning profile
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Tells the analyzer what “good” means for this {targetType} and what to extract from each run, and sets which steps get the learned knowledge and what they search for.
              {detail.config.profile_status === 'none' && ' It is generated automatically on the first cycle, or you can generate it now.'}
              {detail.config.profile_status === 'edited' && ' You have edited this profile — cycles will not overwrite it.'}
            </Typography>
          </Box>
          <Button variant="outlined" startIcon={<AutoAwesomeIcon />} onClick={() => setGenOpen(true)} sx={{ flexShrink: 0 }}>
            {saved ? 'Regenerate' : 'Generate'}
          </Button>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {message && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
        <Stack spacing={2}>
          <TextField label="What this does" multiline minRows={2} value={profile.summary} onChange={(e) => set('summary', e.target.value)} fullWidth />
          <ListField label="A good run…" value={profile.success_criteria} onChange={(v) => set('success_criteria', v)} max={20} />
          <ListField label="Common ways it goes wrong" value={profile.failure_modes} onChange={(v) => set('failure_modes', v)} max={20} />
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          What to remember from each run
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Learnings are stored as “{profile.observation.key_label || 'input'} → {profile.observation.item_label || 'result'}” mappings, plus general facts.
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Key (what is looked up)"
              value={profile.observation.key_label}
              onChange={(e) => setObs('key_label', e.target.value)}
              helperText="e.g. “Requested product”"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Item (what worked for it)"
              value={profile.observation.item_label}
              onChange={(e) => setObs('item_label', e.target.value)}
              helperText="e.g. “Accepted substitute”"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              multiline
              label="How to generalize"
              value={profile.observation.generalize}
              onChange={(e) => setObs('generalize', e.target.value)}
              helperText="e.g. “Record both the exact product and its general type, so learnings apply to similar products.”"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <ListField label="Example keys" value={profile.observation.key_examples} onChange={(v) => setObs('key_examples', v)} splitCommas max={8} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <ListField label="Categories" value={profile.observation.categories} onChange={(v) => setObs('categories', v)} splitCommas max={30} />
          </Grid>
          <Grid item xs={12}>
            <ListField label="Other facts worth remembering" value={profile.fact_topics} onChange={(v) => set('fact_topics', v)} max={20} />
          </Grid>
        </Grid>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          {targetType === 'chain' ? 'Steps that get knowledge, and what they search for' : 'What to search for'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {targetType === 'chain'
            ? 'Each row does two things: it gives learned knowledge to that step, and sets what the step searches the knowledge with. '
            : 'Sets what each run searches the learned knowledge with. '}
          Use {'{{input.field}}'}
          {targetType === 'chain' && <>, {'{{item.field}}'} in steps that run once per item, or {'{{node-N.field}}'}</>}. Leave it empty to search with
          the whole input.{targetType === 'chain' && ' Click a suggested field under a step to insert it.'}{targetType === 'chain' && ' Click a suggested field under a step to insert it.'}
          {targetType === 'chain' &&
            (injectScope.length
              ? ` Settings → Step overrides is set, so only ${injectScope.map((id) => stepLabel(nodes, id)).join(', ')} get knowledge right now; the searches below still apply to those steps.`
              : profile.inject.length
                ? ' Only the steps listed here get knowledge.'
                : ' No steps listed, so every AI step gets knowledge, searched with its whole input.')}
        </Typography>
        {targetType === 'chain' && (
          <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
            Learning reads the output of:{' '}
            {learnScope.length ? learnScope.map((id) => stepLabel(nodes, id)).join(', ') : 'every step'}
            {detail.settings.scope.learn_nodes.length ? ' (set under Settings → Step overrides).' : ' (chosen by this profile; change it under Settings → Step overrides).'}{' '}
            Pick steps whose output shows the final results by name, not only ids.
          </Alert>
        )}
        <Stack spacing={1.5}>
          {profile.inject.map((inj, i) => {
            const fields = nodes.find((n) => n.node_id === inj.node_id)?.fields || [];
            const unused = fields.filter((f) => !inj.query_template.includes(`{{${f}}}`));
            return (
              <Box key={i} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                  {targetType === 'chain' ? (
                    <TextField
                      select
                      size="small"
                      label="Step"
                      value={inj.node_id || ''}
                      onChange={(e) => setInject(i, { node_id: e.target.value })}
                      sx={{ width: { sm: 280 }, flexShrink: 0 }}
                    >
                      {aiNodes.map((n) => (
                        <MenuItem key={n.node_id} value={n.node_id} disabled={n.node_id !== inj.node_id && usedNodes.has(n.node_id)}>
                          {stepLabel(nodes, n.node_id)}
                        </MenuItem>
                      ))}
                    </TextField>
                  ) : (
                    <Chip label={nodeLabel(null)} sx={{ flexShrink: 0 }} />
                  )}
                  <TextField
                    size="small"
                    fullWidth
                    label="Search with"
                    placeholder="Whole input"
                    value={inj.query_template}
                    onChange={(e) => setInject(i, { query_template: e.target.value })}
                    InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
                  />
                  <IconButton onClick={() => set('inject', profile.inject.filter((_, j) => j !== i))} aria-label="Remove" sx={{ alignSelf: { xs: 'flex-end', sm: 'center' } }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
                {unused.length > 0 && (
                  <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Insert:
                    </Typography>
                    {unused.map((f) => (
                      <Chip
                        key={f}
                        size="small"
                        variant="outlined"
                        label={f}
                        onClick={() => insertField(i, f)}
                        sx={{ fontFamily: 'monospace', fontSize: 12 }}
                      />
                    ))}
                  </Stack>
                )}
                {inj.why && (
                  <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
                    {inj.why}
                  </Typography>
                )}
              </Box>
            );
          })}
          <Box>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() =>
                set('inject', [
                  ...profile.inject,
                  {
                    node_id: targetType === 'chain' ? aiNodes.find((n) => !usedNodes.has(n.node_id))?.node_id || null : null,
                    query_template: targetType === 'chain' ? '' : '{{input}}',
                  },
                ])
              }
              disabled={targetType === 'chain' ? aiNodes.every((n) => usedNodes.has(n.node_id)) : profile.inject.length > 0}
            >
              Add
            </Button>
          </Box>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
        <TextField
          label="Notes for the analyzer"
          multiline
          minRows={5}
          fullWidth
          value={profile.notes}
          onChange={(e) => set('notes', e.target.value)}
          inputProps={{ maxLength: 2000 }}
          helperText={`Rules the analyzer should follow when reading runs, e.g. which output field is the key and which is the result. ${profile.notes.length}/2000`}
        />
      </Paper>

      <Stack direction="row" spacing={1} sx={{ position: 'sticky', bottom: 0, py: 1.5, bgcolor: 'background.default' }}>
        <Button variant="contained" onClick={save} disabled={!dirty || saving}>
          {saving ? 'Saving…' : 'Save profile'}
        </Button>
        <Button onClick={() => setProfile(saved || EMPTY)} disabled={!dirty || saving}>
          Discard changes
        </Button>
      </Stack>

      <Dialog open={genOpen} onClose={() => !generating && setGenOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{saved ? 'Regenerate profile' : 'Generate profile'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The analyzer reads this {targetType}'s instructions and structure and drafts a profile. This uses a small amount of credits
            {saved && !refine ? ' and replaces the current profile (including your edits)' : ''}.
          </Typography>
          {saved && (
            <FormControlLabel
              sx={{ mb: 1.5, alignItems: 'flex-start' }}
              control={<Checkbox checked={refine} onChange={(e) => setRefine(e.target.checked)} sx={{ pt: 0.25 }} />}
              label={
                <Box>
                  <Typography variant="body2">Start from the current profile</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Keeps what still fits, including your edits, and changes what your notes ask for. Turn off to draft a new one from scratch.
                  </Typography>
                </Box>
              }
            />
          )}
          <TextField
            label="What should it focus on? (optional)"
            placeholder="e.g. A good substitute is the same product type at a similar price; customers dislike brand changes for baby food."
            multiline
            minRows={3}
            fullWidth
            value={hints}
            onChange={(e) => setHints(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenOpen(false)} disabled={generating}>
            Cancel
          </Button>
          <Button variant="contained" onClick={generate} disabled={generating} startIcon={<AutoAwesomeIcon />}>
            {generating ? 'Generating…' : 'Generate'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
